import { Link, NavLink, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '@reflex/ui/components/ui/popover';
import { ConnectionIndicator } from '@/components/status/ConnectionIndicator';
import { cn } from '@reflex/ui/lib/utils';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { orgBasePath, ROUTES } from '@/lib/routes';
import type { NavItem, NavDrillGroup } from './nav-types';
import { getActiveDrillItemPath } from './nav-utils';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { UserSection } from './UserSection';
import type { WsConnectionState } from '@/lib/socketManager';

interface NavBarCollapsedProps {
  wsState: WsConnectionState;
  onCommandOpen: () => void;
  mainNavItems: NavItem[];
  mainDrillGroups: NavDrillGroup[];
  bottomDrillGroups: NavDrillGroup[];
  pluginBottomItems: NavItem[];
}

const iconBtnBase =
  'flex size-7 items-center justify-center rounded-md transition-all duration-150';
const iconBtnActive = 'bg-selected ring-1 ring-selected-border/40 text-selected-foreground';
const iconBtnInactive =
  'text-sidebar-foreground/70 hover:bg-sidebar-accent/30 hover:text-sidebar-foreground';

function CollapsedNavLink({
  to,
  label,
  icon: Icon,
  iconBadge: IconBadge,
  openInNewWindow,
  isActive: isActiveFn,
  homePath,
}: NavItem & {
  isActive?: (to: string, pathname: string) => boolean;
  homePath?: string;
}) {
  const { pathname } = useLocation();
  const isHome = homePath ? to === homePath : to === '/';
  const active = isActiveFn
    ? isActiveFn(to, pathname)
    : isHome
      ? pathname === to
      : pathname === to || pathname.startsWith(to + '/');

  if (openInNewWindow) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={to}
            target="_blank"
            rel="noreferrer"
            data-testid={`nav-item-${to}`}
            className={cn(iconBtnBase, iconBtnInactive, 'relative')}
            aria-label={`${label} (opens in new window)`}
          >
            <Icon className="h-3.5 w-3.5" />
            {IconBadge && <IconBadge />}
          </a>
        </TooltipTrigger>
        <TooltipContent side="right">{label} ↗</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          data-testid={`nav-item-${to}`}
          data-active={active ? 'true' : undefined}
          className={cn(iconBtnBase, active ? iconBtnActive : iconBtnInactive, 'relative')}
          aria-label={label}
        >
          <Icon className="h-3.5 w-3.5" />
          {IconBadge && <IconBadge />}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function CollapsedDrillGroup({ group }: { group: NavDrillGroup }) {
  const { pathname } = useLocation();
  const groupActive = pathname.startsWith(group.basePath);
  const activeItemPath = getActiveDrillItemPath(group.items, pathname);
  const GroupIcon = group.icon;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`nav-drill-${group.basePath}`}
              data-active={groupActive ? 'true' : undefined}
              className={cn(iconBtnBase, groupActive ? iconBtnActive : iconBtnInactive)}
              aria-label={group.label}
            >
              <GroupIcon className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{group.label}</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="w-44 p-1" sideOffset={8}>
        <div className="flex flex-col gap-0.5">
          {group.items.map(({ to, label, icon: Icon }) => {
            const active = to === activeItemPath;
            return (
              <NavLink
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  active
                    ? 'bg-selected text-selected-foreground font-medium'
                    : 'text-foreground/80 hover:bg-accent/50',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {label}
              </NavLink>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function NavBarCollapsed({
  wsState,
  onCommandOpen,
  mainNavItems,
  mainDrillGroups,
  bottomDrillGroups,
  pluginBottomItems,
}: NavBarCollapsedProps) {
  const slug = useActiveOrgSlug();
  const homePath = slug ? ROUTES.org(slug).home : '/';
  const runsPath = slug ? ROUTES.org(slug).runs : '/runs';
  const agentsPrefix = slug ? `${orgBasePath(slug)}/agents/` : '/agents/';
  return (
    <nav
      aria-label="Main navigation"
      className="flex h-full w-full flex-col items-center bg-sidebar py-2 gap-1"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={homePath} className={cn(iconBtnBase, iconBtnInactive)} aria-label="Go home">
            <ReflexMark variant="tile" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">Go home</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onCommandOpen}
            className={cn(iconBtnBase, iconBtnInactive)}
            aria-label="Search"
          >
            <Search className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Search ⌘K</TooltipContent>
      </Tooltip>

      <div className="flex flex-1 flex-col items-center gap-0.5 min-h-0 px-1 py-0.5 scrollbar-fade">
        {mainNavItems.map((item) => (
          <CollapsedNavLink
            key={item.to}
            {...item}
            homePath={homePath}
            isActive={
              item.to === runsPath
                ? (_to, p) => p.startsWith(runsPath) || p.startsWith(agentsPrefix)
                : undefined
            }
          />
        ))}

        <div className="my-0.5 h-px w-6 bg-border/30" />

        {mainDrillGroups.map((group) => (
          <CollapsedDrillGroup key={group.basePath} group={group} />
        ))}
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <div className="flex size-7 items-center justify-center">
          <ConnectionIndicator state={wsState} iconOnly />
        </div>
        {pluginBottomItems.map((item) => (
          <CollapsedNavLink key={item.to} {...item} />
        ))}
        {bottomDrillGroups.map((group) => (
          <CollapsedDrillGroup key={group.basePath} group={group} />
        ))}
      </div>

      <div className="mx-2 h-px w-6 bg-border/30" />

      <UserSection collapsed />
    </nav>
  );
}
